import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from '@/hooks/use-toast';
import { GenerateSaleExportContext } from '@/lib/contexts/generate-sale-export.context';
import { GenerateNegotiationFileDocument } from '@/lib/graphql/generated/graphql';
import {
  DealItemCreateInput,
  CreateDealItemDocument,
} from '@/lib/graphql/generated/graphql';
import { mapI18nLanguageToLanguageIso6391 } from '@/lib/utils';
import { useMutation } from '@apollo/client/react';

import { useDealNotEditableHandler } from './useDealNotEditableHandler';

// 1. Liste des clés « prix » à multiplier par 100
const priceKeys = [
  'buyingPrice',
  'choosePrice',
  'discountedPrice',
  'listPrice',
  'potentialSalesVolume',
  'shippingPrice',
] as const;

type PriceKey = (typeof priceKeys)[number];

/**
 * Transforme les champs monétaires et pourcentages avant envoi
 */
function transformCreate(item: DealItemCreateInput): DealItemCreateInput {
  const payload: DealItemCreateInput = { ...item };

  // ×100 sur les prix
  priceKeys.forEach((key: PriceKey) => {
    const v = item[key];
    if (typeof v === 'number') {
      payload[key] = Math.round(v * 100);
    }
  });

  // % → fraction
  if (item.chooseDiscount != null) {
    payload.chooseDiscount = item.chooseDiscount / 100;
  }

  if (item.marginPercentage != null) {
    payload.marginPercentage = item.marginPercentage / 100;
  }

  if (item.vatRate != null) {
    payload.vatRate = item.vatRate / 100;
  }

  return payload;
}

/**
 * Hook pour créer un seul DealItem
 */
export function useCreateDealItem(
  dealId: string,
  generateSaleExportOverride = false
) {
  const generateSaleExport =
    useContext(GenerateSaleExportContext) || generateSaleExportOverride;
  const { i18n, t } = useTranslation();
  const [generateFile] = useMutation(GenerateNegotiationFileDocument);
  const [createDealItemMutation, { loading, error, data }] = useMutation(
    CreateDealItemDocument
  );
  const { handleIfDealNotEditable } = useDealNotEditableHandler();

  /**
   * Crée un produit pour le deal donné
   */
  const createItem = async (item: DealItemCreateInput) => {
    const payload = transformCreate(item);
    const result = await createDealItemMutation({
      variables: {
        dealId,
        item: payload,
      },
    });

    const mutationResult = result.data?.createDealItem;
    if (mutationResult?.__typename === 'DomainException') {
      handleIfDealNotEditable(mutationResult.code);
      return { id: undefined };
    }

    const id =
      mutationResult?.__typename === 'MutationSuccess'
        ? mutationResult.id
        : undefined;
    if (id && generateSaleExport) {
      try {
        const generated = await generateFile({
          variables: {
            dealId,
            language: mapI18nLanguageToLanguageIso6391(i18n.language),
          },
        });
        if (
          generated.data?.exportDealSelection.__typename !== 'MutationSuccess'
        )
          throw new Error('negotiation file generation failed');
      } catch {
        toast({
          description: t('negotiation-file.error'),
          variant: 'destructive',
        });
      }
    }
    return { id };
  };

  return { createItem, loading, error, data };
}
